import React from "react";
import {
	TextField,
	type TextFieldProps,
	IconButton,
	type IconButtonProps,
	InputAdornment,
	styled,
} from "@mui/material";
import { useIntl } from "react-intl";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const StyledTextField = styled(TextField)({
	"& input[type=number]": {
		MozAppearance: "textfield",
	},
	"& input[type=number]::-webkit-outer-spin-button": {
		WebkitAppearance: "none",
		margin: 0,
	},
	"& input[type=number]::-webkit-inner-spin-button": {
		WebkitAppearance: "none",
		margin: 0,
	},
});

export interface NumberFieldProps extends Omit<TextFieldProps, "type" | "onChange" | "value"> {
	readonly value: number | null;
	readonly onChange: (value: number | null) => void;
	readonly min?: number;
	readonly max?: number;
	readonly step?: number;
	readonly allowDecimals?: boolean;
	readonly decimalPlaces?: number;
	readonly hideSteppers?: boolean;
	readonly "data-testid"?: string;
	readonly slotProps?: TextFieldProps["slotProps"] & {
		incrementButton?: Partial<IconButtonProps>;
		decrementButton?: Partial<IconButtonProps>;
	};
}

export const NumberField: React.FC<NumberFieldProps> = ({
	value,
	onChange,
	min,
	max,
	step = 1,
	allowDecimals = false,
	decimalPlaces = 2,
	hideSteppers = false,
	disabled,
	size: sizeProp,
	"data-testid": testId = "number-field",
	...textFieldProps
}) => {
	const intl = useIntl();

	const handleIncrement = () => {
		const currentValue = value ?? 0;
		const newValue = currentValue + step;

		if (max !== undefined && newValue > max) {
			onChange(max);
			return;
		}

		onChange(newValue);
	};

	const handleDecrement = () => {
		const currentValue = value ?? 0;
		const newValue = currentValue - step;

		if (min !== undefined && newValue < min) {
			onChange(min);
			return;
		}

		onChange(newValue);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = event.target.value;

		if (inputValue === "" || inputValue === "-") {
			onChange(null);
			return;
		}

		const numericValue = allowDecimals ? parseFloat(inputValue) : parseInt(inputValue, 10);

		if (isNaN(numericValue)) {
			return;
		}

		if (min !== undefined && numericValue < min) {
			onChange(min);
			return;
		}

		if (max !== undefined && numericValue > max) {
			onChange(max);
			return;
		}

		onChange(numericValue);
	};

	const handleBlur = () => {
		if (value === null) {
			if (min !== undefined) {
				onChange(min);
				return;
			}

			onChange(0);
			return;
		}

		if (allowDecimals && decimalPlaces !== undefined) {
			const rounded = parseFloat(value.toFixed(decimalPlaces));
			onChange(rounded);
		}
	};

	const displayValue = value === null ? "" : value.toString();
	const size = sizeProp || "medium";

	const isIncrementDisabled = disabled || (max !== undefined && (value ?? 0) >= max);
	const isDecrementDisabled = disabled || (min !== undefined && (value ?? 0) <= min);

	const stepperButtons = (
		<InputAdornment position="end" sx={{ marginRight: -1 }}>
			<IconButton
				size={size}
				onClick={handleDecrement}
				disabled={isDecrementDisabled}
				tabIndex={-1}
				data-testid={`${testId}-decrement`}
				aria-label={intl.formatMessage({
					id: "number-field.decrement",
					defaultMessage: "Decrement",
				})}
				{...textFieldProps.slotProps?.decrementButton}
			>
				<RemoveIcon fontSize="small" />
			</IconButton>
			<IconButton
				size={size}
				onClick={handleIncrement}
				disabled={isIncrementDisabled}
				tabIndex={-1}
				data-testid={`${testId}-increment`}
				aria-label={intl.formatMessage({
					id: "number-field.increment",
					defaultMessage: "Increment",
				})}
				{...textFieldProps.slotProps?.incrementButton}
			>
				<AddIcon fontSize="small" />
			</IconButton>
		</InputAdornment>
	);

	return (
		<StyledTextField
			{...textFieldProps}
			size={size}
			disabled={disabled}
			type="number"
			value={displayValue}
			onChange={handleChange}
			onBlur={handleBlur}
			data-testid={testId}
			slotProps={{
				...textFieldProps.slotProps,
				htmlInput: {
					...textFieldProps.slotProps?.htmlInput,
					"data-testid": `${testId}-input`,
					min,
					max,
					step,
				},
				input: {
					...textFieldProps.slotProps?.input,
					endAdornment: hideSteppers ? undefined : stepperButtons,
				},
			}}
		/>
	);
};
